import { useEffect, useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Alert, IconButton, Snackbar, Tooltip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import {
    Device,
    DeviceRouteCardSummary,
    RouteCard,
    RouteCardOperationsUpdateResponse,
} from '../../interfaces';
import { getRouteCard } from '../../api/getRouteCard';
import { postRouteCard } from '../../api/postRouteCard';
import { getRouteCardPdfDownloadLink } from '../../api/getRouteCardPdfDownloadLink';
import { EditRouteCardDialog } from '../EditRouteCardDialog';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { patchOnboardDeviceById } from '../../store/devices.reducer';
import { selectUserNickname } from '../../store/user.reducer';
import { downloadFileFromUrl } from '../../utils/downloadFileFromUrl';

const EDITABLE_DEVICE_STATUS = 'InProgressPhase2Done';
const VIEWABLE_DEVICE_STATUS = 'Done';

interface RoadMapCellProps extends GridRenderCellParams {
    onReload: () => void;
}

export const RoadMapCell = (cellValues: RoadMapCellProps) => {
    const device = cellValues.row as Device;
    const { onReload } = cellValues;
    const dispatch = useAppDispatch();
    const userNickname = useAppSelector(selectUserNickname);

    const [pendingSummary, setPendingSummary] = useState<DeviceRouteCardSummary | null>(null);
    const [editingRouteCard, setEditingRouteCard] = useState<RouteCard | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [nicknameError, setNicknameError] = useState<boolean>(false);

    useEffect(() => {
        if (device.routeCard) {
            setPendingSummary(null);
        }
    }, [device.routeCard]);

    const routeCardSummary = device.routeCard ?? pendingSummary;
    const hasTemplate = Boolean(device.organizationDetails?.routeCardTemplateName);
    const isEditable = device.status === EDITABLE_DEVICE_STATUS && hasTemplate;
    const isViewable = device.status === VIEWABLE_DEVICE_STATUS && Boolean(routeCardSummary);
    const isPdfReady = isViewable && Boolean(routeCardSummary?.pdfUrl);

    const handleClose = () => {
        setEditingRouteCard(null);
    };

    const handleSuccess = (result: RouteCardOperationsUpdateResponse) => {
        setEditingRouteCard(null);

        if (!device.deviceId) {
            return;
        }

        dispatch(
            patchOnboardDeviceById({
                deviceId: device.deviceId,
                patch: {
                    routeCard: {
                        id: result.routeCard.id,
                        status: result.routeCard.status,
                        pdfUrl: result.routeCard.pdfUrl,
                    },
                    ...(result.device && {
                        status: result.device.status,
                        updatedAt: result.device.updatedAt ?? undefined,
                    }),
                },
            }),
        );
    };

    const openExisting = async (id: string) => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        try {
            const card = await getRouteCard(id);
            setEditingRouteCard(card);
        } finally {
            setIsLoading(false);
        }
    };

    const viewPdf = async (routeCardId: string) => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        try {
            const presignedUrl = await getRouteCardPdfDownloadLink(routeCardId);
            const keyParts = presignedUrl.split('/');
            await downloadFileFromUrl(presignedUrl, keyParts[keyParts.length - 1]);
        } finally {
            setIsLoading(false);
        }
    };

    const createAndOpen = async () => {
        if (isLoading || !device.deviceId) {
            return;
        }

        if (!userNickname) {
            setNicknameError(true);
            return;
        }

        setIsLoading(true);
        try {
            const created = await postRouteCard({ deviceId: device.deviceId });
            setPendingSummary({ id: created.id, status: created.status, pdfUrl: created.pdfUrl });
            setEditingRouteCard(created);
            onReload();
        } finally {
            setIsLoading(false);
        }
    };

    const handleClick = () => {
        if (isLoading || (!isEditable && !isViewable)) {
            return;
        }

        if (isPdfReady && routeCardSummary) {
            viewPdf(routeCardSummary.id);

            return;
        }

        if (routeCardSummary) {
            openExisting(routeCardSummary.id);
            return;
        }

        createAndOpen();
    };

    const tooltip = isPdfReady
        ? 'Переглянути PDF маршрутної карти'
        : isViewable
        ? 'Перегляд завершеної маршрутної карти'
        : routeCardSummary
        ? 'Редагувати маршрутну карту'
        : 'Створити маршрутну карту';

    if (!isEditable && !isViewable) {
        return null;
    }

    const icon = isPdfReady ? (
        <PictureAsPdfIcon fontSize="small" color="success" />
    ) : routeCardSummary ? (
        <EditIcon fontSize="small" color="error" />
    ) : (
        <EditIcon fontSize="small" sx={{ color: 'action.disabled' }} />
    );

    return (
        <>
            <Tooltip title={tooltip}>
                <span>
                    <IconButton size="small" onClick={handleClick} disabled={isLoading}>
                        {icon}
                    </IconButton>
                </span>
            </Tooltip>

            {editingRouteCard && (
                <EditRouteCardDialog
                    open={Boolean(editingRouteCard)}
                    routeCard={editingRouteCard}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                />
            )}

            <Snackbar
                open={nicknameError}
                autoHideDuration={5000}
                onClose={() => setNicknameError(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity="error" onClose={() => setNicknameError(false)}>
                    User must have a nickname to create route maps
                </Alert>
            </Snackbar>
        </>
    );
};
